// Phone features, each with a web fallback. None of them throws at the
// caller for a refused permission or a missing feature: they return null (or
// false) and the screen carries on without.
import { Geolocation } from '@capacitor/geolocation';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { isNative } from '../config.js';

// Where the phone is now: { lat, lng, accuracy } or null (refused, no fix in
// time, or no GPS). Only while the app is open; never in the background.
export async function currentPosition({ timeout = 10_000 } = {}) {
  try {
    const opts = { enableHighAccuracy: true, timeout, maximumAge: 30_000 };
    const p = isNative
      ? await Geolocation.getCurrentPosition(opts)
      : await new Promise((ok, no) => (navigator.geolocation ? navigator.geolocation.getCurrentPosition(ok, no, opts) : no(new Error('none'))));
    return { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null };
  } catch {
    return null;
  }
}

export const canScan = isNative;

// Scan one barcode or QR code with Google's scanner (it fetches itself from
// Play the first time). The code, or null if they backed out or it isn't
// ready yet.
export async function scanBarcode() {
  if (!isNative) return null;
  try {
    const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
    if (!available) {
      await BarcodeScanner.installGoogleBarcodeScannerModule();
      return null; // downloading in the background; they tap Scan again
    }
    const { barcodes } = await BarcodeScanner.scan();
    return barcodes[0]?.rawValue || null;
  } catch {
    return null;
  }
}

const toBase64 = (blob) => new Promise((ok, no) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).split(',')[1]);
  r.onerror = () => no(r.error);
  r.readAsDataURL(blob);
});
const safeName = (name) => String(name || 'file').replace(/[^\w. -]+/g, '_').slice(0, 80);

async function toCacheFile(blob, name) {
  const { uri } = await Filesystem.writeFile({ path: `out/${Date.now()}-${safeName(name)}`, data: await toBase64(blob), directory: Directory.Cache, recursive: true });
  return uri;
}

// Open a file (a PDF report, a photo) in the phone's own viewer: the app's
// web view can't show PDFs. On the web it downloads.
export async function openFile(blob, name) {
  try {
    if (!isNative) {
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: safeName(name) }).click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return true;
    }
    await FileOpener.openFile({ path: await toCacheFile(blob, name), mimeType: blob.type || undefined });
    return true;
  } catch {
    return false;
  }
}

// Share a file (WhatsApp, email, Teams...). false if it can't be shared here.
export async function shareFile(blob, name, title) {
  try {
    if (!isNative) {
      const file = new File([blob], safeName(name), { type: blob.type });
      if (!navigator.canShare?.({ files: [file] })) return false;
      await navigator.share({ files: [file], title });
      return true;
    }
    await Share.share({ title, files: [await toCacheFile(blob, name)], dialogTitle: title });
    return true;
  } catch {
    return false;
  }
}

// A web page outside the app (the phone's browser, over the app).
export async function openExternal(url) {
  try {
    if (isNative) await Browser.open({ url });
    else window.open(url, '_blank', 'noopener');
  } catch { /* nothing to open it with */ }
}

// Keep the screen on (a long step); off again when done.
export async function keepAwake(on) {
  if (!isNative) return;
  try { await (on ? KeepAwake.keepAwake() : KeepAwake.allowSleep()); } catch { /* not supported */ }
}

// A small buzz on an important tap.
export async function buzz() {
  if (!isNative) return;
  try { await Haptics.impact({ style: ImpactStyle.Light }); } catch { /* no vibration */ }
}
